import { Controller } from "@hotwired/stimulus"
import { anchorPanel } from "#lib/floating_panel"

// Entity link hover-cards. Attach to any container holding entity links; the
// controller delegates hover over `a[data-hover-slug]` inside it, so links added
// later (Turbo frame swaps) are covered without re-binding. On hover it fetches
// /e/<slug>/hover once (cached for the page's life), floats the returned
// ".floating-panel" near the link, and hides it when the pointer leaves both the
// link and the card.
//
//   <div data-controller="hover-card">… prose / member list with
//        <a href="/e/lena-cho" data-hover-slug="lena-cho">Lena Cho</a> …</div>
//
// The link element itself is the Floating-UI reference — no caret math, unlike
// the picker; both anchor through the same lib/floating_panel helper (the picker
// hands it a virtual caret rect, we hand it a real element).
export default class extends Controller {
  static values = { delay: { type: Number, default: 150 }, hideDelay: { type: Number, default: 120 } }

  connect() {
    this.cache = new Map() // slug -> panel HTML string
    this.panel = null // the live floating element, or null
    this.cleanup = null // anchorPanel teardown
    this.showTimer = null
    this.hideTimer = null

    this.onEnter = this.onEnter.bind(this)
    this.onLeave = this.onLeave.bind(this)
    this.element.addEventListener("mouseover", this.onEnter)
    this.element.addEventListener("mouseout", this.onLeave)
  }

  disconnect() {
    this.element.removeEventListener("mouseover", this.onEnter)
    this.element.removeEventListener("mouseout", this.onLeave)
    this.clearTimers()
    this.hide()
  }

  // --- hover detection (delegated) ----------------------------------------

  // The link under the pointer, or null. Ignores moves that aren't over a
  // hover-able entity link.
  linkFrom(event) {
    const link = event.target.closest("a[data-hover-slug]")
    return link && this.element.contains(link) ? link : null
  }

  onEnter(event) {
    const link = this.linkFrom(event)
    if (!link) return
    // Cancel a pending hide (e.g. moving from one link straight to another).
    clearTimeout(this.hideTimer)
    clearTimeout(this.showTimer)
    this.showTimer = setTimeout(() => this.show(link), this.delayValue)
  }

  onLeave(event) {
    if (!this.linkFrom(event)) return
    // Don't yank the card if the pointer is heading onto the card itself; the
    // card's own mouseenter cancels this hide.
    clearTimeout(this.showTimer)
    this.scheduleHide()
  }

  // --- show / hide --------------------------------------------------------

  async show(link) {
    const slug = link.dataset.hoverSlug
    if (!slug) return

    const html = await this.fetchCard(slug)
    if (!html) return // 404 or network error — show nothing

    this.hide() // replace any open card
    this.mount(html, link)
  }

  mount(html, link) {
    const tpl = document.createElement("template")
    tpl.innerHTML = html.trim()
    const panel = tpl.content.firstElementChild
    if (!panel) return

    // Floating-UI fixed-position setup (mirrors the picker): pin to origin,
    // max-content width so a container can't resize it mid-measure.
    Object.assign(panel.style, {
      position: "fixed", top: "0", left: "0", width: "max-content", zIndex: "1000"
    })
    // Keep the card alive while the pointer is on it; leaving it hides.
    panel.addEventListener("mouseenter", () => clearTimeout(this.hideTimer))
    panel.addEventListener("mouseleave", () => this.scheduleHide())

    document.body.appendChild(panel)
    this.panel = panel
    const arrowEl = panel.querySelector(".floating-panel-arrow")
    // Anchor directly to the link element (a real DOM reference — no caret math).
    this.cleanup = anchorPanel(link, panel, arrowEl, { placement: "top" })
  }

  scheduleHide() {
    clearTimeout(this.hideTimer)
    this.hideTimer = setTimeout(() => this.hide(), this.hideDelayValue)
  }

  hide() {
    if (this.cleanup) {
      this.cleanup()
      this.cleanup = null
    }
    if (this.panel) {
      this.panel.remove()
      this.panel = null
    }
  }

  clearTimers() {
    clearTimeout(this.showTimer)
    clearTimeout(this.hideTimer)
  }

  // --- fetch (cached) -----------------------------------------------------

  async fetchCard(slug) {
    if (this.cache.has(slug)) return this.cache.get(slug)
    try {
      const res = await fetch(`/e/${encodeURIComponent(slug)}/hover`, {
        headers: { Accept: "text/html" }
      })
      if (!res.ok) {
        this.cache.set(slug, null) // remember the miss; don't refetch
        return null
      }
      const html = await res.text()
      this.cache.set(slug, html)
      return html
    } catch {
      return null // transient network error — don't cache, allow a retry
    }
  }
}
